import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { Activity, AlertTriangle, CheckCircle2, ShieldOff, Brain, MessageCircleQuestion, GitCompare, RotateCcw, FlagTriangleRight } from "lucide-react";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { ShadowModeBanner, ShadowMetricBadge } from "@/components/admin/ShadowModeBanner";

type GoldenRun = {
  id: string;
  run_label: string;
  total_cases: number;
  passed: number;
  failed: number;
  critical_failures: number;
  pass_rate: number | null;
  duration_ms: number | null;
  created_at: string;
  details?: {
    metrics?: {
      persona_consistency_score?: number;
      cross_layer_conflict_rate?: number;
      modulation_within_bounds_rate?: number;
    };
    gates?: {
      persona_consistency_score?: boolean;
      cross_layer_conflict_rate?: boolean;
      modulation_within_bounds_rate?: boolean;
    };
  } | null;
};

type CalibrationRun = {
  id: string;
  task_class: string;
  ece: number | null;
  brier: number | null;
  sample_size: number;
  alert_triggered: boolean;
  created_at: string;
};

type MetacogDecision = {
  decision: string;
  task_class: string | null;
  pre_response_confidence: number;
  created_at: string;
};

type EventRow = { event_type: string; count: number };
type ReleaseScorecard = { overall_score: number; created_at: string; by_dimension: Record<string, unknown> | null };
type ReleaseFlag = { flag_key: string; enabled: boolean; updated_at: string };
type VersionEntry = { entity_type: string; to_state: string | null; created_at: string };
type ReportEntry = { report_type: string; created_at: string; executive_summary: string | null };
type TomContributionRun = { id: string; contribution_score: number | null; created_at: string; winner: string | null };
type TomCalibrationRun = { id: string; brier_score: number | null; ece: number | null; created_at: string; sample_size: number };
type GroupMood = { id: string; mood_label: string; confidence: number; valence: number; arousal: number; window_end: string };
type PromptTrace = { id: string; composition_latency_ms: number | null; created_at: string };
type DriftEvent = { id: string; severity: string | null; created_at: string };
type AlertDispatch = { id: string; priority: string; created_at: string; payload: { kind?: string } | null };

export default function QualityDashboard() {
  const { data: orgId } = useCurrentOrgId();
  const [goldenRuns, setGoldenRuns] = useState<GoldenRun[]>([]);
  const [calibrationRuns, setCalibrationRuns] = useState<CalibrationRun[]>([]);
  const [metacog, setMetacog] = useState<MetacogDecision[]>([]);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [hallucination24h, setHallucination24h] = useState(0);
  const [releaseScorecards, setReleaseScorecards] = useState<ReleaseScorecard[]>([]);
  const [releaseFlags, setReleaseFlags] = useState<ReleaseFlag[]>([]);
  const [versionEntries, setVersionEntries] = useState<VersionEntry[]>([]);
  const [releaseReports, setReleaseReports] = useState<ReportEntry[]>([]);
  const [tomContributionRuns, setTomContributionRuns] = useState<TomContributionRun[]>([]);
  const [tomCalibrations, setTomCalibrations] = useState<TomCalibrationRun[]>([]);
  const [groupMoods, setGroupMoods] = useState<GroupMood[]>([]);
  const [promptTraces, setPromptTraces] = useState<PromptTrace[]>([]);
  const [driftEvents, setDriftEvents] = useState<DriftEvent[]>([]);
  const [rolloutAlerts, setRolloutAlerts] = useState<AlertDispatch[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!orgId) {
        if (!cancelled) {
          setGoldenRuns([]);
          setCalibrationRuns([]);
          setMetacog([]);
          setEvents([]);
          setHallucination24h(0);
          setReleaseScorecards([]);
          setReleaseFlags([]);
          setVersionEntries([]);
          setReleaseReports([]);
          setTomContributionRuns([]);
          setTomCalibrations([]);
          setGroupMoods([]);
          setPromptTraces([]);
          setDriftEvents([]);
          setRolloutAlerts([]);
          setLoading(false);
        }
        return;
      }
      const since = new Date(Date.now() - 24 * 3600_000).toISOString();
      const [g, c, m, e, h, sc, flags, versions, reports, contribution, calibration, moods, traces, drifts, alerts] = await Promise.all([
        supabase.from("golden_set_runs").select("*").eq("org_id", orgId).order("created_at", { ascending: false }).limit(10),
        supabase.from("confidence_calibration_runs").select("*").eq("org_id", orgId).order("created_at", { ascending: false }).limit(20),
        supabase.from("metacog_runtime_decisions").select("decision, task_class, pre_response_confidence, created_at").eq("org_id", orgId).order("created_at", { ascending: false }).limit(50),
        supabase.from("cortex_events").select("event_type").eq("org_id", orgId).gte("occurred_at", since).in("event_type", ["tom.signal.applied","metacog.abstain","metacog.clarify","memory.ambiguity.detected","provider_verification_blocked"]),
        supabase.from("hallucination_events").select("id", { count: "exact", head: true }).eq("org_id", orgId).gte("created_at", since),
        supabase.from("sentinel_scorecards").select("overall_score, created_at, by_dimension").eq("org_id", orgId).order("created_at", { ascending: false }).limit(12),
        supabase.from("cortex_feature_flags").select("flag_key, enabled, updated_at, updated_by").order("updated_at", { ascending: false }).limit(30),
        supabase.from("sentinel_version_history").select("entity_type, to_state, created_at").eq("org_id", orgId).order("created_at", { ascending: false }).limit(30),
        supabase.from("sentinel_reports").select("report_type, created_at, executive_summary").eq("org_id", orgId).order("created_at", { ascending: false }).limit(10),
        supabase.from("tom_contribution_runs").select("id, contribution_score, created_at, winner").eq("org_id", orgId).order("created_at", { ascending: false }).limit(8),
        supabase.from("tom_calibration").select("id, brier_score, ece, created_at, sample_size").eq("org_id", orgId).order("created_at", { ascending: false }).limit(8),
        supabase.from("syra_group_mood").select("id, mood_label, confidence, valence, arousal, window_end").eq("org_id", orgId).order("window_end", { ascending: false }).limit(8),
        supabase.from("syra_prompt_composition_traces").select("id, composition_latency_ms, created_at").eq("org_id", orgId).order("created_at", { ascending: false }).limit(50),
        supabase.from("syra_persona_drift_events").select("id, severity, created_at").eq("org_id", orgId).order("created_at", { ascending: false }).limit(20),
        supabase.from("alert_dispatches").select("id, priority, created_at, payload").eq("org_id", orgId).order("created_at", { ascending: false }).limit(20),
      ]);
      if (cancelled) return;
      setGoldenRuns((g.data as GoldenRun[]) ?? []);
      setCalibrationRuns((c.data as CalibrationRun[]) ?? []);
      setMetacog((m.data as MetacogDecision[]) ?? []);
      const tally: Record<string, number> = {};
      (e.data ?? []).forEach((row: any) => { tally[row.event_type] = (tally[row.event_type] ?? 0) + 1; });
      setEvents(Object.entries(tally).map(([event_type, count]) => ({ event_type, count })));
      setHallucination24h((h as any).count ?? 0);
      setReleaseScorecards((sc.data as ReleaseScorecard[]) ?? []);
      setReleaseFlags((flags.data as ReleaseFlag[]) ?? []);
      setVersionEntries((versions.data as VersionEntry[]) ?? []);
      setReleaseReports((reports.data as ReportEntry[]) ?? []);
      setTomContributionRuns((contribution.data as TomContributionRun[]) ?? []);
      setTomCalibrations((calibration.data as TomCalibrationRun[]) ?? []);
      setGroupMoods((moods.data as GroupMood[]) ?? []);
      setPromptTraces((traces.data as PromptTrace[]) ?? []);
      setDriftEvents((drifts.data as DriftEvent[]) ?? []);
      setRolloutAlerts((alerts.data as AlertDispatch[]) ?? []);
      setLoading(false);
    };
    load();
    const t = setInterval(load, 30_000);
    return () => { cancelled = true; clearInterval(t); };
  }, [orgId]);

  const lastGolden = goldenRuns[0];
  const passRate = lastGolden?.pass_rate != null ? Number(lastGolden.pass_rate) * 100 : null;
  const goldenMetrics = lastGolden?.details?.metrics ?? null;
  const goldenGates = lastGolden?.details?.gates ?? null;
  const avgTomContribution = tomContributionRuns.length
    ? tomContributionRuns.reduce((sum, run) => sum + Number(run.contribution_score ?? 0), 0) / tomContributionRuns.length
    : null;
  const avgTomBrier = tomCalibrations.length
    ? tomCalibrations.reduce((sum, run) => sum + Number(run.brier_score ?? 0), 0) / tomCalibrations.length
    : null;
  const latestMood = groupMoods[0] ?? null;
  const promptLatencyP95 = useMemo(() => {
    const values = promptTraces
      .map((trace) => Number(trace.composition_latency_ms ?? 0))
      .filter((value) => Number.isFinite(value) && value > 0)
      .sort((a, b) => a - b);
    if (!values.length) return null;
    const index = Math.min(values.length - 1, Math.max(0, Math.ceil(values.length * 0.95) - 1));
    return values[index];
  }, [promptTraces]);
  const recentCriticalDrift = useMemo(() => driftEvents.filter((event) => event.severity === "critical").length, [driftEvents]);
  const phase3AlertCount = useMemo(
    () => rolloutAlerts.filter((alert) => {
      const kind = alert.payload?.kind ?? "";
      return kind === "persona_drift_critical" || kind === "persona_golden_regression" || kind === "tom_calibration_alert";
    }).length,
    [rolloutAlerts],
  );
  const avgEce = calibrationRuns.length
    ? calibrationRuns.reduce((a, r) => a + (Number(r.ece) || 0), 0) / calibrationRuns.length
    : null;
  const abstainCount = metacog.filter(d => d.decision === "abstain").length;
  const clarifyCount = metacog.filter(d => d.decision === "clarify").length;
  const executeCount = metacog.filter(d => d.decision === "execute").length;
  const totalMetacog = metacog.length;
  const releaseSummary = useMemo(() => {
    const latest = releaseScorecards[0];
    const baseline = releaseScorecards[1];
    const scoreDelta = latest && baseline ? Number(latest.overall_score) - Number(baseline.overall_score) : null;
    const liveFlags = releaseFlags.filter((flag) => flag.enabled);
    const proposalDrafts7d = versionEntries.filter((entry) => entry.entity_type === "proposal" && entry.to_state === "draft" && Date.now() - new Date(entry.created_at).getTime() <= 7 * 24 * 3600_000).length;
    const latestReport = releaseReports[0] ?? null;
    return { latest, baseline, scoreDelta, liveFlags, proposalDrafts7d, latestReport };
  }, [releaseFlags, releaseReports, releaseScorecards, versionEntries]);
  const phase3Readiness = useMemo(() => {
    const checks = [
      { label: "ToM contribution > 0.20", flag: "feature.tom_v2", ok: avgTomContribution != null && avgTomContribution > 0.2, value: avgTomContribution != null ? avgTomContribution.toFixed(2) : "—" },
      { label: "ToM Brier ≤ 0.20", flag: "feature.tom_v2", ok: avgTomBrier != null && avgTomBrier <= 0.2, value: avgTomBrier != null ? avgTomBrier.toFixed(2) : "—" },
      { label: "Persona consistency ≥ 0.85", flag: "feature.persona_v19", ok: Number(goldenMetrics?.persona_consistency_score ?? 0) >= 0.85, value: goldenMetrics?.persona_consistency_score != null ? Number(goldenMetrics.persona_consistency_score).toFixed(2) : "—" },
      { label: "Cross-layer conflict ≤ 3%", flag: "feature.persona_modulator", ok: Number(goldenMetrics?.cross_layer_conflict_rate ?? 1) <= 0.03, value: goldenMetrics?.cross_layer_conflict_rate != null ? `${(Number(goldenMetrics.cross_layer_conflict_rate) * 100).toFixed(1)}%` : "—" },
      { label: "Within bounds ≥ 98%", flag: "feature.persona_modulator", ok: Number(goldenMetrics?.modulation_within_bounds_rate ?? 0) >= 0.98, value: goldenMetrics?.modulation_within_bounds_rate != null ? `${(Number(goldenMetrics.modulation_within_bounds_rate) * 100).toFixed(1)}%` : "—" },
      { label: "Prompt p95 ≤ 150ms", flag: "feature.prompt_composition_trace", ok: promptLatencyP95 != null && promptLatencyP95 <= 150, value: promptLatencyP95 != null ? `${promptLatencyP95.toFixed(0)}ms` : "—" },
      { label: "Sem drift crítico recente", flag: "feature.persona_drift_detector", ok: recentCriticalDrift === 0, value: String(recentCriticalDrift) },
      { label: "Sem alertas críticos pendentes", flag: null as string | null, ok: phase3AlertCount === 0, value: String(phase3AlertCount) },
    ];
    const passed = checks.filter((check) => check.ok).length;
    return { checks, passed, total: checks.length };
  }, [avgTomBrier, avgTomContribution, goldenMetrics, phase3AlertCount, promptLatencyP95, recentCriticalDrift]);

  if (loading) {
    return (
      <div className="container mx-auto p-6 space-y-4">
        <Skeleton className="h-12 w-1/2" />
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Activity className="w-8 h-8" /> Quality Dashboard
        </h1>
        <p className="text-muted-foreground mt-1">
          Métricas de qualidade da Syra: golden tests, calibração de confiança, gates de metacognição e alucinação.
        </p>
        {!orgId ? <p className="text-sm text-muted-foreground mt-2">Nenhuma organização ativa encontrada para carregar a telemetria.</p> : null}
      </div>

      <ShadowModeBanner context="Use a página Flags & Rollout para promover gates aprovados." />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> Golden pass rate</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{passRate != null ? `${passRate.toFixed(1)}%` : "—"}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {lastGolden ? `${lastGolden.passed}/${lastGolden.total_cases} (last run)` : "sem runs"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Brain className="w-4 h-4" /> ECE médio (calib)</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{avgEce != null ? avgEce.toFixed(3) : "—"}</div>
            <p className="text-xs text-muted-foreground mt-1">{calibrationRuns.length} runs / {"<"} 0.10 ideal</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><ShieldOff className="w-4 h-4" /> Abstain rate (metacog)</CardTitle></CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totalMetacog ? `${((abstainCount / totalMetacog) * 100).toFixed(0)}%` : "0%"}</div>
            <p className="text-xs text-muted-foreground mt-1">{abstainCount} de {totalMetacog} decisões</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Alucinações (24h)</CardTitle></CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${hallucination24h > 0 ? "text-destructive" : ""}`}>{hallucination24h}</div>
            <p className="text-xs text-muted-foreground mt-1">eventos detectados</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="release">
        <TabsList>
          <TabsTrigger value="release">Release</TabsTrigger>
          <TabsTrigger value="metacog">Metacognition</TabsTrigger>
          <TabsTrigger value="events">Cortex Events (24h)</TabsTrigger>
          <TabsTrigger value="golden">Golden Set</TabsTrigger>
          <TabsTrigger value="calibration">Calibração</TabsTrigger>
        </TabsList>

        <TabsContent value="release" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><GitCompare className="w-4 h-4" /> Readiness atual</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{releaseSummary.latest ? Number(releaseSummary.latest.overall_score).toFixed(2) : "—"}</div>
                <p className="text-xs text-muted-foreground mt-1">vs baseline {releaseSummary.baseline ? Number(releaseSummary.baseline.overall_score).toFixed(2) : "—"}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Activity className="w-4 h-4" /> Delta release</CardTitle></CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${releaseSummary.scoreDelta != null && releaseSummary.scoreDelta < 0 ? "text-destructive" : ""}`}>
                  {releaseSummary.scoreDelta != null ? `${releaseSummary.scoreDelta >= 0 ? "+" : ""}${releaseSummary.scoreDelta.toFixed(2)}` : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Comparação com o snapshot anterior</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FlagTriangleRight className="w-4 h-4" /> Flags em rollout</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{releaseSummary.liveFlags.length}</div>
                <p className="text-xs text-muted-foreground mt-1">flags habilitadas (binário)</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Drafts / ajustes</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{releaseSummary.proposalDrafts7d}</div>
                <p className="text-xs text-muted-foreground mt-1">Propostas geradas nos últimos 7 dias</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">ToM contribution</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{avgTomContribution != null ? avgTomContribution.toFixed(2) : "—"}</div>
                <p className="text-xs text-muted-foreground mt-1">meta &gt; 0.20 para avanço</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">ToM Brier</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{avgTomBrier != null ? avgTomBrier.toFixed(2) : "—"}</div>
                <p className="text-xs text-muted-foreground mt-1">meta ≤ 0.20</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Group mood atual</CardTitle></CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{latestMood?.mood_label ?? "—"}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {latestMood ? `conf. ${Number(latestMood.confidence ?? 0).toFixed(2)} · val ${Number(latestMood.valence ?? 0).toFixed(2)}` : "sem leitura recente"}
                </p>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle>Release Comparison</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded border p-4 space-y-3">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-medium">Fase 3 — readiness operacional</p>
                    <p className="text-xs text-muted-foreground">Checklist de promoção para shadow/canary/closeout.</p>
                  </div>
                  <Badge variant={phase3Readiness.passed === phase3Readiness.total ? "default" : "secondary"}>
                    {phase3Readiness.passed}/{phase3Readiness.total} gates
                  </Badge>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                  {phase3Readiness.checks.map((check) => (
                    <div key={check.label} className="flex items-center justify-between rounded border px-3 py-2 text-sm gap-3">
                      <span className="flex items-center gap-2">
                        {check.label}
                        {check.flag ? <ShadowMetricBadge flagKey={check.flag} /> : null}
                      </span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">{check.value}</span>
                        <Badge variant={check.ok ? "default" : "destructive"}>{check.ok ? "ok" : "fail"}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium">Flags habilitadas</p>
                  {releaseSummary.liveFlags.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nenhuma flag habilitada no momento.</p>
                  ) : (
                    releaseSummary.liveFlags.slice(0, 8).map((flag) => (
                      <div key={flag.flag_key} className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                        <code className="font-mono text-xs">{flag.flag_key}</code>
                        <Badge variant="default">on</Badge>
                      </div>
                    ))
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">Resumo executivo mais recente</p>
                  <div className="border rounded p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Badge variant="outline">{releaseSummary.latestReport?.report_type ?? "sem report"}</Badge>
                      <span className="text-xs text-muted-foreground">{releaseSummary.latestReport ? new Date(releaseSummary.latestReport.created_at).toLocaleString("pt-BR") : "—"}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {releaseSummary.latestReport?.executive_summary?.slice(0, 420) ?? "Sem relatório executivo recente para comparação."}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="metacog" className="space-y-4">
          <Card>
            <CardHeader><CardTitle>Distribuição de decisões (últimas 50)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {totalMetacog === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma decisão de metacog ainda. Mande uma mensagem ambígua no WhatsApp para gerar.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm"><span>Execute</span><span>{executeCount}</span></div>
                    <Progress value={(executeCount / totalMetacog) * 100} />
                    <div className="flex justify-between text-sm"><span>Clarify</span><span>{clarifyCount}</span></div>
                    <Progress value={(clarifyCount / totalMetacog) * 100} />
                    <div className="flex justify-between text-sm"><span>Abstain</span><span>{abstainCount}</span></div>
                    <Progress value={(abstainCount / totalMetacog) * 100} />
                  </div>
                  <div className="border rounded-md divide-y mt-4 max-h-80 overflow-auto">
                    {metacog.slice(0, 20).map((d, i) => (
                      <div key={i} className="p-2 flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant={d.decision === "abstain" ? "destructive" : d.decision === "clarify" ? "secondary" : "default"}>
                            {d.decision}
                          </Badge>
                          <span className="text-muted-foreground">{d.task_class ?? "—"}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="font-mono">{Number(d.pre_response_confidence).toFixed(2)}</span>
                          <span className="text-xs text-muted-foreground">{new Date(d.created_at).toLocaleTimeString("pt-BR")}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader><CardTitle>Eventos Cortex nas últimas 24h</CardTitle></CardHeader>
            <CardContent>
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem eventos dos novos gates ainda.</p>
              ) : (
                <div className="space-y-2">
                  {events.map(e => (
                    <div key={e.event_type} className="flex justify-between p-2 border rounded">
                      <span className="font-mono text-sm">{e.event_type}</span>
                      <Badge>{e.count}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="golden">
          <Card>
            <CardHeader><CardTitle>Últimos 10 runs do Golden Set</CardTitle></CardHeader>
            <CardContent>
              {goldenMetrics ? (
                <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="rounded border p-3 text-sm">
                    <div className="text-xs text-muted-foreground">Persona consistency</div>
                    <div className="text-xl font-bold">{Number(goldenMetrics.persona_consistency_score ?? 0).toFixed(2)}</div>
                    <Badge variant={goldenGates?.persona_consistency_score ? "default" : "destructive"}>{goldenGates?.persona_consistency_score ? "gate ok" : "gate fail"}</Badge>
                  </div>
                  <div className="rounded border p-3 text-sm">
                    <div className="text-xs text-muted-foreground">Cross-layer conflict</div>
                    <div className="text-xl font-bold">{`${(Number(goldenMetrics.cross_layer_conflict_rate ?? 0) * 100).toFixed(1)}%`}</div>
                    <Badge variant={goldenGates?.cross_layer_conflict_rate ? "default" : "destructive"}>{goldenGates?.cross_layer_conflict_rate ? "gate ok" : "gate fail"}</Badge>
                  </div>
                  <div className="rounded border p-3 text-sm">
                    <div className="text-xs text-muted-foreground">Within bounds</div>
                    <div className="text-xl font-bold">{`${(Number(goldenMetrics.modulation_within_bounds_rate ?? 0) * 100).toFixed(1)}%`}</div>
                    <Badge variant={goldenGates?.modulation_within_bounds_rate ? "default" : "destructive"}>{goldenGates?.modulation_within_bounds_rate ? "gate ok" : "gate fail"}</Badge>
                  </div>
                </div>
              ) : null}
              {goldenRuns.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem runs registrados.</p>
              ) : (
                <div className="space-y-2">
                  {goldenRuns.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <div className="font-medium">{r.run_label}</div>
                        <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("pt-BR")}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={r.critical_failures > 0 ? "destructive" : "default"}>
                          {r.passed}/{r.total_cases}
                        </Badge>
                        <span className="text-sm font-mono">
                          {r.pass_rate != null ? `${(Number(r.pass_rate) * 100).toFixed(1)}%` : "—"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="calibration">
          <Card>
            <CardHeader><CardTitle>Confidence Calibration (últimos 20)</CardTitle></CardHeader>
            <CardContent>
              {calibrationRuns.length === 0 ? (
                <p className="text-sm text-muted-foreground">Sem runs de calibração.</p>
              ) : (
                <div className="space-y-2">
                  {calibrationRuns.map(r => (
                    <div key={r.id} className="flex items-center justify-between p-3 border rounded">
                      <div>
                        <div className="font-medium">{r.task_class}</div>
                        <div className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleString("pt-BR")} — n={r.sample_size}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        {r.alert_triggered && <Badge variant="destructive">alert</Badge>}
                        <span className="text-sm font-mono">ECE {r.ece != null ? Number(r.ece).toFixed(3) : "—"}</span>
                        <span className="text-xs font-mono text-muted-foreground">Brier {r.brier != null ? Number(r.brier).toFixed(3) : "—"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
